import { GameVars } from "../../../GameVars";
import { Block } from "../Block";
import { BlocksDispenser } from "../BlocksDispenser";
import { BoardContainer } from "../BoardContainer";
import { BoardManager } from "../BoardManager";
import { BlocksScrollingModel } from "./BlocksScrolinglModel";
import { AudioManager } from "../../../AudioManager";
import { GUI } from "../GUI";
import * as Templok from "@ravalmatic/templok-engine";

export class Niche extends Phaser.GameObjects.Container {

    public static readonly SCROLLING_SPEED = 7.5; // era 7.5
    public static readonly ROTATION_TIME = 400;

    public static currentInstance: Niche;

    public block: Block;
    
    private rotationContainer: Phaser.GameObjects.Container;
    private wheel: Phaser.GameObjects.Image;
    private reRollPieceContainer: Phaser.GameObjects.Container;
    private scrolling: boolean;
    private blocksDispenser: BlocksDispenser;
    private scrollingBlocks: Block[];
    private randomBlockDataArray: Templok.BlockData [];
    private prng: Templok.PRNG;
    private rotatedBlockData: Templok.BlockData;
    private blocksScrollingModel: BlocksScrollingModel;
    private glow: Phaser.GameObjects.Image;
    private glowTween: Phaser.Tweens.Tween;
    private glowing: boolean;
    private holdLabel: Phaser.GameObjects.Text;

    constructor(scene: Phaser.Scene, x: number, y: number, blocksDispenser: BlocksDispenser) {
        
        super(scene);

        Niche.currentInstance = this;

        this.x = x;
        this.y = y;
        this.blocksDispenser = blocksDispenser;

        this.scrolling = false;
        this.block = null;
        this.scrollingBlocks = [];
        this.prng = new Templok.PRNG(GameVars.seed);
        this.rotatedBlockData = null;
        this.glowTween = null;
        this.glowing = false;

        this.wheel = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_2", "stone_wheel");
        this.add(this.wheel);

        this.glow = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_2", "stone_wheel_glow");
        this.glow.visible = false;
        this.add(this.glow);

        this.rotationContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.rotationContainer);

        this.reRollPieceContainer = new Phaser.GameObjects.Container(this.scene);
        this.add(this.reRollPieceContainer);

        const border = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_2", "stone_wheel_light");
        this.add(border);
        
        const mask = new Phaser.GameObjects.Image(this.scene, 0, 0, "texture_atlas_2", "stone_wheel_mask");
        this.add(mask);

        const maskShape = new Phaser.GameObjects.Graphics(this.scene);
        maskShape.fillStyle(0x00FF00);
        maskShape.fillCircle(0, 0, 82);
        maskShape.x = 679; 
        maskShape.y = (this.y + BlocksDispenser.PY) * GameVars.scaleY + BoardContainer.currentInstance.y;
        maskShape.scaleY = GameVars.scaleY;
        
        this.reRollPieceContainer.mask = maskShape.createGeometryMask();        
        
        this.setRandomBlocksArray();

        this.holdLabel = new Phaser.GameObjects.Text(this.scene, 0, 0, "HOLD", {fontFamily: "Futura-Medium", fontSize: "24px", color: "#FFFFFF"});
        this.holdLabel.setOrigin(.5);
        this.add(this.holdLabel);

        this.scene.tweens.add({
            targets: this.holdLabel,
            alpha: .65,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 500,
            repeat: -1,
            yoyo: true
        });
    }

    public update(): void {

        if (this.block && this.scrolling) {

            this.blocksScrollingModel.update(false);

            for (let i = 0; i < this.scrollingBlocks.length; i++) {

                const block = this.scrollingBlocks[i];
                const scrollingData = this.blocksScrollingModel.getScrollingData(block.blockData.id);
                block.y = scrollingData.y;
            }

            if (!this.blocksScrollingModel.scrolling) {

                this.scrolling = false;

                this.onBlockReRolled();
            }
        }
    }

    public onGameStarted(): void {

        if (GameVars.templokEngine.blockOnNicheData) {
            const block = new Block(this.scene, <BlocksDispenser> this.parentContainer, GameVars.templokEngine.blockOnNicheData, 1, 0, 0);
            this.addBlock(block, true);
        }
    }

    public playTickAudioEffect(): void {

        AudioManager.playSound("piece_rerolled");
    }

    public addBlock(block: Block, restoredGameBlock?: boolean): void {

        if (this.holdLabel) {
            this.holdLabel.destroy();
            this.holdLabel = null;
        }

        this.block = block;

        this.block.x -= this.x;
        this.block.y -= this.y;

        if (block.parentContainer) {
            block.parentContainer.remove(block);
        }

        this.add(block);

        this.block.goToNiche(restoredGameBlock);

        this.deactivateGlow();

        if (block.blockData.n < 3) {
            AudioManager.playSound("piece_small_released");
        } else if (block.blockData.n < 5) {
            AudioManager.playSound("piece_medium_released");
        } else {
            AudioManager.playSound("piece_large_released");
        }

        const data = block.blockData.data;

        if (data[0] === data[1] && data[0] === data[2] && data[0] === data[3]) {
            GUI.currentInstance.deactivateSpin();
        }
    }

    public removeBlock(): void {

        this.block = null;

        GUI.currentInstance.activateSpin();
    }

    public reRoll(blockData: Templok.BlockData): void {

        this.scrolling = true;

        this.block.enable();

        this.scrollingBlocks = [this.block];

        this.block.parentContainer.remove(this.block);
        this.reRollPieceContainer.add(this.block);

        // cogemos 4 bloques random que no coincidan con el que se va a dar al jugador
        // ponemos el bloque en cuestión en un array y hacemos shuffle
        let tmpBlockData: Templok.BlockData[] = [];

        this.shuffleArray(this.randomBlockDataArray);

        // el nuevo bloque
        tmpBlockData.push(blockData);

        let n = 0;
        let i = 0;

        while (n < 4) {

            const randomBlockData = this.randomBlockDataArray[i];

            if (randomBlockData.data[randomBlockData.i] !== blockData.data[blockData.i] && randomBlockData.data[randomBlockData.i] !== this.block.blockData.data[this.block.blockData.i]) {
                n ++;
                randomBlockData.id = blockData.id + n;
                tmpBlockData.push(randomBlockData);
            }

            i ++;
        }

        this.shuffleArray(tmpBlockData);

        for (let i = 0; i < tmpBlockData.length; i ++) {

            const block = new Block (this.scene, this.blocksDispenser, tmpBlockData[i], this.block.color, 0, 0, true);
            this.reRollPieceContainer.add(block);

            if (block.blockData === blockData) {
                this.block = block;
            }

            this.scrollingBlocks.push(block);
        }

        // posicionar los bloques en vertical
        for (let i = 1; i < this.scrollingBlocks.length; i ++) {

            let py = -(this.scrollingBlocks[0].blockHeight + 1) * BoardContainer.CELL_SIZE * .5 * Block.SCALE_ON_DISPENSER;

            for (let j = 1; j < i; j ++) {
                py -= (this.scrollingBlocks[j].blockHeight + 1) * BoardContainer.CELL_SIZE * Block.SCALE_ON_DISPENSER;
            }

            py -= (this.scrollingBlocks[i].blockHeight + 1) * BoardContainer.CELL_SIZE * .5 * Block.SCALE_ON_DISPENSER;

            this.scrollingBlocks[i].y += py;
        }

        // emular
        this.blocksScrollingModel = new BlocksScrollingModel(this.block.blockData.id);

        let f: number;

        for (f = 100; f < 1000; f ++) {

            this.blocksScrollingModel.reset(this.scrollingBlocks, f);

            while (this.blocksScrollingModel.scrolling) {
                this.blocksScrollingModel.update(true);
            }

            if (this.blocksScrollingModel.success) {
                break;
            }
        }

        this.blocksScrollingModel.reset(this.scrollingBlocks, f);

        this.glow.visible = true;
        this.glow.alpha = 0;

        this.scene.tweens.add({
            targets: this.glow,
            alpha: 1,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 500,
            onComplete: function(): void {
                this.glowTween = this.scene.tweens.add({
                    targets: this.glow,
                    alpha: .65,
                    ease: Phaser.Math.Easing.Cubic.InOut,
                    duration: 500,
                    repeat: -1,
                    yoyo: true
                });
            },
            onCompleteScope: this
        });
    }

    public rotate(blockData: Templok.BlockData): void {

        this.rotatedBlockData = blockData;

        this.block.parentContainer.remove(this.block);
        this.rotationContainer.add(this.block);

        const a = this.wheel.angle;

        this.scene.tweens.add({
            targets: [this.wheel, this.glow],
            angle: a + 90,
            ease: Phaser.Math.Easing.Cubic.InOut,
            duration: Niche.ROTATION_TIME
        });

        this.scene.tweens.add({
            targets: this.rotationContainer,
            angle: 90,
            ease: Phaser.Math.Easing.Cubic.InOut,
            duration: Niche.ROTATION_TIME,
            onComplete: this.onRotationComplete,
            onCompleteScope: this
        });

        this.block.onRotation();

        this.glow.visible = true;
        this.glow.alpha = 0;

        this.scene.tweens.add({
            targets: this.glow,
            alpha: 1,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: Niche.ROTATION_TIME / 2
        });
    }

    public activateGlow(): void {

        if (this.glowing) {
            return;
        }
        this.glowing = true;

        this.glow.visible = true;
        this.glow.alpha = 0;

        this.scene.tweens.add({
            targets: this.glow,
            alpha: .75,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 350
        });
    }

    public deactivateGlow(): void {

        this.scene.tweens.add({
            targets: this.glow,
            alpha: 0,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 250,
            onComplete: function(): void {
                this.glowing = false;
                this.glow.visible = false;
            },
            onCompleteScope: this
        });
    }

    private onRotationComplete(): void {

        this.rotationContainer.angle = 0;

        const rotatedBlock = new Block(this.scene, this.blocksDispenser, this.rotatedBlockData, this.block.color, 0, 0, true);
        rotatedBlock.isOnNiche = true;
        this.add(rotatedBlock);

        this.block.destroy();
        this.block = rotatedBlock;

        this.blocksDispenser.checkBlocks();

        this.scene.tweens.add({
            targets: this.glow,
            alpha: 0,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 500,
            onComplete: function(): void {
                this.glow.visible = false;
            },
            onCompleteScope: this
        });

        BoardManager.currentInstance.onRotationCompleted();

        AudioManager.playSound("piece_turn");
    }

    private onBlockReRolled(): void {

        this.block.y = this.block.py;
                
        this.block.isOnNiche = true;
        this.block.parentContainer.remove(this.block);
        this.add(this.block);
        
        for (let i = 0; i < this.scrollingBlocks.length; i++) {

            const block = this.scrollingBlocks[i];

            if (block !== this.block) {
                block.removeFakeBlockFromNiche();
            }   
        }

        this.glowTween.remove();
        this.glowTween = null;
        
        this.scene.tweens.add({
            targets: this.glow,
            alpha: 0,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 500,
            onComplete: function(): void {
                this.glow.visible = false;
            },
            onCompleteScope: this
        });

        const data = this.block.blockData.data;

        if (data[0] === data[1] && data[0] === data[2] && data[0] === data[3]) {
            GUI.currentInstance.deactivateSpin();
        } else {
            GUI.currentInstance.activateSpin();
        }

        BoardManager.currentInstance.onBlockReRolled();   
    }

    private setRandomBlocksArray(): void {

        this.randomBlockDataArray = [];

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_1_1.n,
            data: Templok.BlockType.B_1_1.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_2_1.n,
            data: Templok.BlockType.B_2_1.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_3_1.n,
            data: Templok.BlockType.B_3_1.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_3_2.n,
            data: Templok.BlockType.B_3_2.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_1.n,
            data: Templok.BlockType.B_4_1.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_2.n,
            data: Templok.BlockType.B_4_2.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_3.n,
            data: Templok.BlockType.B_4_3.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_4.n,
            data: Templok.BlockType.B_4_4.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_5.n,
            data: Templok.BlockType.B_4_5.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_6.n,
            data: Templok.BlockType.B_4_6.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_4_7.n,
            data: Templok.BlockType.B_4_7.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_1.n,
            data: Templok.BlockType.B_5_1.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_2.n,
            data: Templok.BlockType.B_5_2.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_3.n,
            data: Templok.BlockType.B_5_3.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_4.n,
            data: Templok.BlockType.B_5_4.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_5.n,
            data: Templok.BlockType.B_5_5.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_6.n,
            data: Templok.BlockType.B_5_6.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_7.n,
            data: Templok.BlockType.B_5_7.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_8.n,
            data: Templok.BlockType.B_5_8.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_9.n,
            data: Templok.BlockType.B_5_9.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_10.n,
            data: Templok.BlockType.B_5_10.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });

        this.randomBlockDataArray.push({
            id: -1,
            n: Templok.BlockType.B_5_11.n,
            data: Templok.BlockType.B_5_11.data,
            i: Math.floor(this.prng.getRandom() * 3)
        });
    }

    private shuffleArray(array: any []): void {

        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(this.prng.getRandom() * (i + 1));
            const temp = array[i];
            array[i] = array[j];
            array[j] = temp;
        }
    }
}